import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { UserPlus, Loader2, Wallet, Image as ImageIcon, Trophy, Crown, ArrowRight, ArrowLeft, Info, Check, Camera, X } from "lucide-react";
import { api, BACKEND_URL } from "@/lib/api";
import { PLATFORMS } from "@/lib/platforms";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// Steps: 1 = Rules, 2 = Photo + Profile Fields, 3 = Bid to reach #1
export default function RegisterProfileModal({ open, onClose, onOpenBidModal }) {
  const [step, setStep] = useState(1);
  const [platform, setPlatform] = useState("Instagram");
  const [handle, setHandle] = useState("");
  const [url, setUrl] = useState("");
  const [tagline, setTagline] = useState("");
  const [payoutWallet, setPayoutWallet] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [newProfile, setNewProfile] = useState(null);
  const [bidInfo, setBidInfo] = useState({ current_top_bid: 0, amount_to_be_number_1: 5 });
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setStep(1);
      setPlatform("Instagram"); setHandle(""); setUrl(""); setTagline("");
      setPayoutWallet(""); setPhotoFile(null); setPhotoPreview(null);
      setNewProfile(null);
      api.get("/bid-info").then((r) => setBidInfo(r.data)).catch(() => {});
    }
  }, [open]);

  const onPickPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) return toast.error("Máximo 3 MB");
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) return toast.error("Solo JPEG, PNG o WEBP");
    setPhotoFile(f);
    setPhotoPreview(URL.createObjectURL(f));
  };

  const registerProfile = async () => {
    if (!handle.trim()) return toast.error("Handle requerido");
    if (!url.trim() || !/^https?:\/\//.test(url)) return toast.error("URL debe iniciar con http:// o https://");
    if (!tagline.trim()) return toast.error("Tagline requerido");
    if (!payoutWallet.trim()) return toast.error("Payout wallet requerido");
    setSubmitting(true);
    try {
      const { data } = await api.post("/profiles/register", {
        platform_type: platform, handle_name: handle.trim(), profile_url: url.trim(),
        custom_tagline: tagline.trim(), payout_wallet: payoutWallet.trim(),
      });
      // upload photo (optional but recommended)
      if (photoFile) {
        const fd = new FormData(); fd.append("file", photoFile);
        try {
          await api.post(`/profiles/${data.profile_id}/photo`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch { toast.error("Perfil creado, pero la foto no se subió"); }
      }
      setNewProfile({ id: data.profile_id, handle_name: handle, platform_type: platform });
      toast.success(data.already_registered ? "Perfil ya existía" : "¡Perfil registrado!");
      setStep(3);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setSubmitting(false); }
  };

  const goToPujar = () => {
    onClose();
    if (newProfile) onOpenBidModal?.(newProfile);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="register-profile-modal" className="bg-void-modal border-neon-cyan/40 max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em] text-neon-cyan mb-2">
            <UserPlus className="w-3.5 h-3.5" />
            REGISTRO DE PARTICIPANTE · PASO {step}/3
          </div>
          <DialogTitle className="font-heading text-2xl font-black tracking-tight">
            {step === 1 && "Bases y proceso de puja"}
            {step === 2 && "Tu foto y tu perfil"}
            {step === 3 && "Puja para tomar el #1"}
          </DialogTitle>
          <DialogDescription>
            {step === 1 && "Antes de registrarte, lee cómo funciona el concurso."}
            {step === 2 && "Sube una foto que te representará en el ranking + tus datos."}
            {step === 3 && "Este es el monto que necesitas para quedarte en el trono."}
          </DialogDescription>
        </DialogHeader>

        {/* Step progress */}
        <div className="flex items-center gap-1 mt-2 mb-4">
          {[1, 2, 3].map((n) => (
            <div key={n} className="flex-1 h-1 rounded-full overflow-hidden bg-white/10">
              <div className="h-full bg-neon-cyan transition-all" style={{ width: step >= n ? "100%" : "0%" }} />
            </div>
          ))}
        </div>

        {/* STEP 1 - RULES */}
        {step === 1 && (
          <div className="space-y-4">
            <RuleBlock icon={Trophy} color="#FFB800" title="1. Registra tu perfil (gratis)">
              Súbelo con tu foto, red social y tagline. Aparece en la lista con $0.00.
            </RuleBlock>
            <RuleBlock icon={Crown} color="#00F0FF" title="2. Puja para escalar al #1">
              Tres tiers: <b>Push Up +$1</b>, <b>Viral Boost +$3</b>, o{" "}
              <b>God Mode</b> = top actual + $5 (te pone en el #1 con corona dorada animada).
            </RuleBlock>
            <RuleBlock icon={Info} color="#FF003C" title="3. El pozo se reparte en 72h">
              50% para el creador #1 al cierre + 10% para el mejor viralizador. Pagos en 48h hábiles al wallet que registres.
            </RuleBlock>
            <RuleBlock icon={Check} color="#25D366" title="4. Anti-sniping y transparencia">
              Toda puja en los últimos 2 minutos extiende el timer +60 segundos. Servidor central UTC. No hay reembolsos.
            </RuleBlock>
            <Button
              data-testid="register-step1-next"
              onClick={() => setStep(2)}
              className="w-full bg-neon-cyan hover:bg-neon-cyan/90 text-black font-bold rounded-full py-6"
            >
              Entiendo, continuar <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}

        {/* STEP 2 - PHOTO + PROFILE */}
        {step === 2 && (
          <div className="space-y-4">
            {/* Photo upload */}
            <div className="rounded-xl border border-neon-cyan/30 bg-neon-cyan/5 p-4">
              <Label className="text-xs font-mono uppercase tracking-widest text-neon-cyan flex items-center gap-2 mb-3">
                <Camera className="w-3.5 h-3.5" /> Tu foto (aparece en el ranking)
              </Label>
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="register-photo-button"
                  className="w-24 h-24 rounded-xl border-2 border-dashed border-neon-cyan/50 bg-black flex items-center justify-center overflow-hidden hover:border-neon-cyan"
                >
                  {photoPreview ? (
                    <img src={photoPreview} alt="preview" className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="w-8 h-8 text-neon-cyan/60" />
                  )}
                </button>
                <div className="flex-1">
                  <p className="text-xs text-white/80 mb-2">
                    JPEG, PNG o WEBP. Máx. 3 MB. {photoFile ? <span className="text-neon-cyan">✓ {photoFile.name}</span> : "Recomendado 1:1"}
                  </p>
                  {photoFile && (
                    <button
                      onClick={() => { setPhotoFile(null); setPhotoPreview(null); }}
                      className="text-xs font-mono text-neon-red hover:underline inline-flex items-center gap-1"
                    >
                      <X className="w-3 h-3" /> Quitar
                    </button>
                  )}
                </div>
              </div>
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp"
                onChange={onPickPhoto} className="hidden" data-testid="register-photo-input" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Red Social</Label>
                <Select value={platform} onValueChange={setPlatform}>
                  <SelectTrigger data-testid="register-platform-select" className="mt-1.5 bg-black border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-void-card border-white/10">
                    {PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Handle / Usuario</Label>
                <Input data-testid="register-handle-input" value={handle} onChange={(e) => setHandle(e.target.value)}
                  placeholder="@usuario" maxLength={60} className="mt-1.5 bg-black border-white/10" />
              </div>
            </div>

            <div>
              <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">URL completa del perfil</Label>
              <Input data-testid="register-url-input" value={url} onChange={(e) => setUrl(e.target.value)}
                placeholder="https://instagram.com/tu_perfil" className="mt-1.5 bg-black border-white/10" />
            </div>

            <div>
              <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                Tagline <span className="ml-2 text-white/40">{tagline.length}/60</span>
              </Label>
              <Input data-testid="register-tagline-input" value={tagline}
                onChange={(e) => setTagline(e.target.value.slice(0, 60))}
                placeholder="Cuéntanos por qué mereces el trono" maxLength={60}
                className="mt-1.5 bg-black border-white/10" />
            </div>

            <div className="rounded-xl border border-neon-gold/30 bg-neon-gold/5 p-4">
              <Label className="text-xs font-mono uppercase tracking-widest text-neon-gold flex items-center gap-2">
                <Wallet className="w-3.5 h-3.5" /> Payment Account (para tus premios)
              </Label>
              <Input data-testid="register-wallet-input" value={payoutWallet}
                onChange={(e) => setPayoutWallet(e.target.value)}
                placeholder="PayPal email o CLABE bancaria" className="mt-1.5 bg-black border-neon-gold/30" />
              <p className="text-[11px] text-muted-foreground mt-2">Guardado cifrado con Fernet. Aquí recibes el 50% si ganas.</p>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(1)} className="border-white/15 rounded-full">
                <ArrowLeft className="w-4 h-4 mr-2" /> Atrás
              </Button>
              <Button data-testid="register-submit-button" disabled={submitting} onClick={registerProfile}
                className="flex-1 bg-neon-cyan hover:bg-neon-cyan/90 text-black font-bold rounded-full py-6">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                  <>Registrar y ver la puja <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* STEP 3 - GO PUJAR */}
        {step === 3 && newProfile && (
          <div className="space-y-4">
            <div className="rounded-2xl border-2 border-neon-gold/40 bg-gradient-to-br from-neon-gold/10 to-transparent p-6 text-center">
              <Crown className="w-10 h-10 text-neon-gold mx-auto mb-3 animate-glow-gold" />
              <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-neon-gold mb-2">
                MONTO PARA SER EL #1
              </div>
              <div className="font-mono font-black text-5xl shine-text tabular-nums leading-none">
                ${bidInfo.amount_to_be_number_1.toFixed(2)}
              </div>
              <div className="text-xs font-mono text-muted-foreground mt-3 uppercase tracking-widest">
                (Top actual ${bidInfo.current_top_bid.toFixed(2)} + $5.00)
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 text-center">
              <MiniPrice label="Push Up" price={bidInfo.push_up_amount} color="#00F0FF" />
              <MiniPrice label="Viral Boost" price={bidInfo.viral_boost_amount} color="#FF003C" />
              <MiniPrice label="God Mode" price={bidInfo.amount_to_be_number_1} color="#FFB800" gold />
            </div>

            <p className="text-sm text-white/80 leading-relaxed">
              Tu perfil <span className="font-bold text-white">{newProfile.handle_name}</span> ya está en la lista.
              Elige un tier y paga con Stripe o SPEI. Si haces <b>God Mode</b>, apareces al instante en el <b className="text-neon-gold">puesto #1</b> y todos bajan un lugar.
            </p>

            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} className="border-white/15 rounded-full">
                Después
              </Button>
              <Button data-testid="register-go-bid-button" onClick={goToPujar}
                className="flex-1 bg-neon-gold hover:bg-neon-gold/90 text-black font-bold rounded-full py-6"
                style={{ boxShadow: "0 0 20px rgba(255,184,0,0.4)" }}>
                <Crown className="w-4 h-4 mr-2" /> PUJAR AHORA
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RuleBlock({ icon: Icon, color, title, children }) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-xl border border-white/5 bg-black/50">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: `${color}15`, border: `1px solid ${color}40`, color }}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1">
        <div className="font-heading font-bold text-sm mb-0.5" style={{ color }}>{title}</div>
        <p className="text-xs text-white/80 leading-relaxed">{children}</p>
      </div>
    </div>
  );
}

function MiniPrice({ label, price, color, gold }) {
  return (
    <div className={`rounded-xl border p-3 ${gold ? "border-neon-gold/40 bg-neon-gold/5" : "border-white/10 bg-black/50"}`}>
      <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color }}>{label}</div>
      <div className="font-mono font-bold text-lg" style={{ color }}>${price.toFixed(2)}</div>
    </div>
  );
}
